"use client";

import ShareActions from "@/components/ShareActions";
import posthog from "posthog-js";
import { sendGTMEvent } from "@next/third-parties/google";
import { useAuthContext } from "@/context/AuthContext";
import {
  getInviteIntro,
  getInviteUrl,
  getWhatsappMessage,
  type InviteKind
} from "@/lib/inviteHelpers";

interface ShareInviteCompactProps {
  inviteToken: string;
  /** Message context - affects the pre-filled share message */
  context: "student-to-parent" | "parent-to-student";
  /** Optional expiration date to display */
  expiresAt?: string;
  /** Which route the share URL points at. Defaults to the parent-student
   *  invite route; pass "gift" for the anonymous gift-claim flow. */
  kind?: InviteKind;
}

export default function ShareInviteCompact({
  inviteToken,
  context,
  expiresAt,
  kind = "parent-student"
}: ShareInviteCompactProps) {
  const { userData, isLoggedIn } = useAuthContext();
  const hasUsedTrial = Boolean(isLoggedIn && userData?.trial_expired);

  const inviteUrl =
    typeof window !== "undefined" ? getInviteUrl(inviteToken, kind) : "";

  const whatsappMessage = getWhatsappMessage(context, inviteUrl, hasUsedTrial);
  const nativeText = getInviteIntro(context, hasUsedTrial);

  const shareTitle =
    context === "student-to-parent"
      ? "WisWiz premium uitnodiging"
      : "WisWiz account activatie";

  return (
    <div className="flex flex-col items-center gap-4 w-full">
      <ShareActions
        url={inviteUrl}
        message={whatsappMessage}
        nativeTitle={shareTitle}
        nativeText={nativeText}
        layout="compact"
        whatsappLabel="Deel uitnodiging via WhatsApp"
        nativeLabel="Meer opties om te delen"
        copiedLabel="Gekopieerd"
        onWhatsAppShared={() => {
          posthog.capture("share_invite_whatsapp", {
            context,
            source: "share_invite_compact"
          });
          sendGTMEvent({ event: "share_invite_whatsapp" });
        }}
        onNativeShareOutcome={(outcome, err) => {
          posthog.capture("share_invite_native", {
            context,
            source: "share_invite_compact",
            open_result: `native_share_${outcome}`,
            ...(outcome === "failed" && {
              error_type: err instanceof Error ? err.name : typeof err
            })
          });
          if (outcome === "completed") {
            sendGTMEvent({ event: "share_invite_native" });
          }
        }}
        onCopySuccess={() => {
          posthog.capture("share_invite_copy_link", {
            context,
            source: "share_invite_compact"
          });
          sendGTMEvent({ event: "share_invite_copy_link" });
        }}
        onCopyFailure={(err) => {
          posthog.capture("share_invite_copy_failed", {
            context,
            source: "share_invite_compact",
            error_type: err instanceof Error ? err.name : typeof err
          });
        }}
      />

      {/* Expiration date */}
      {expiresAt && (
        <p className="text-xs text-gray-500">
          Uitnodiging verloopt op{" "}
          {new Date(expiresAt).toLocaleDateString("nl-NL", {
            day: "numeric",
            month: "long",
            year: "numeric"
          })}
        </p>
      )}
    </div>
  );
}
